import './edit-family-member-modal.scss'

import React, { FormEvent, useCallback, useEffect, useState } from 'react'
import { Modal } from '@entur/modal'
import { GridContainer, GridItem } from '@entur/grid'
import { TextField } from '@entur/form'
import { ChoiceChip } from '@entur/chip'
import { PrimaryButton, SecondaryButton } from '@entur/button'
import { BannerAlertBox, SmallAlertBox, useToast } from '@entur/alert'
import { Employee, FamilyMember, FetchState, TicketTypePreference } from '../../api-clients/api-types'
import { useApi } from '../../api-clients/client-hooks'
import {
  patchCustomerPreference,
  patchFamilyMember,
  postFamilyMember,
  postTicketRights,
} from '../../api-clients/eba-clients'
import { isSeniorCitizenByAge, tolLocalDateFormat } from '../../utils/date-utils'
import { trackEvent } from '../../utils/tracking-utils'
import ValidatableDatePicker from '../form/validatable-date-picker'
import { FormField, formIsValid, validateAllFields, ValidationFormField } from '../../utils/form-utils'
import {
  dateOfBirthFormField,
  firstNameFormField,
  surnameFormField,
  ticketTypeFormField,
} from '../../utils/common-form-fields'
import { ValidatableTextField } from '../form/validatable-text-field'
import {
  ticketTypePreference,
  ticketTypeAdultText,
  ticketTypePreferenceItems,
  ticketTypeSeniorCitizenText,
} from '../../hooks/use-customer-preferences'
import { TicketTypeDropdown } from '../form/dropdowns'
import { OptionalTooltipWrapper } from '../tooltip/OptionalTooltipWrapper'
import { ValidatableChoiceChipGroup } from '../form/validatable-choice-chip-group'

type State = {
  firstName: ValidationFormField<string>
  surname: ValidationFormField<string>
  relation: ValidationFormField<string>
  dateOfBirth: ValidationFormField<Date | undefined>
  ssn: ValidationFormField<string>
  code: FormField<string>
  ticketType: ValidationFormField<string>
  [key: string]: unknown
}

const body = (state: State) => {
  return {
    firstName: state.firstName.value,
    surname: state.surname.value,
    dateOfBirth: tolLocalDateFormat(state.dateOfBirth.value || new Date()), // This is validated
    ssn: state.ssn.value !== '' ? state.ssn.value : undefined,
    relation: { code: state.relation.value, description: '' },
  }
}

function initialState(familyMember?: FamilyMember): State {
  return {
    firstName: firstNameFormField(familyMember?.firstName || ''),
    surname: surnameFormField(familyMember?.surname || ''),
    relation: {
      value: familyMember?.relation.code || '',
      variant: '',
      feedback: '',
      fieldName: 'relation',
      displayName: 'Relasjon',
      validation: {
        required: true,
      },
    },
    dateOfBirth: dateOfBirthFormField(familyMember?.dateOfBirth ? new Date(familyMember?.dateOfBirth) : undefined),
    ssn: {
      value: familyMember?.ssn || '',
      variant: '',
      feedback: '',
      fieldName: 'ssn',
      displayName: 'Fødselsnummer',
      validation: {
        pattern: '^(\\d{5})?$',
        errorMessage: 'Fødselsnummer skal bestå av 5 tall',
        required: false,
      },
    },
    code: {
      value: familyMember?.ticketHolderCode || '',
      fieldName: 'code',
      displayName: 'Kode',
    },
    ticketType: ticketTypeFormField(
      familyMember?.customerPreferences?.ticketTypePreference == TicketTypePreference.SENIOR_CITIZEN
        ? ticketTypeSeniorCitizenText
        : ticketTypeAdultText
    ),
  }
}

type EditFamilyMemberModalProps = {
  open?: boolean
  employee: Employee
  familyMember?: FamilyMember
  close: (shouldUpdate: boolean) => void
}

const EditFamilyMemberModal: React.FC<EditFamilyMemberModalProps> = (props) => {
  const { addToast } = useToast()
  const [state, setState] = useState<State>(initialState())
  const [triedToSave, setTriedToSave] = useState(false)

  const successCloseModal = useCallback(() => {
    props.close(true)
    addToast({
      title: props.familyMember ? 'Familiemedlem endret!' : 'Familiemedlem lagt til!',
      content: 'De er nå tilgjengelig under fanen "Familiemedlemmer".',
    })
    setTriedToSave(false)
  }, [props, addToast, setTriedToSave])

  // Ticket rights
  const [ticketRightsResponse, generateTicketRights] = useApi(postTicketRights, {
    successCallback: () => {
      successCloseModal()
    },
  })

  // Customer preferences (isSeniorCitizen)
  const [patchPreferenceResponse, doPatchPreference] = useApi(patchCustomerPreference)

  const successCallback = (_data: FamilyMember) => {
    generateTicketRights({
      id: props.employee.id,
      body: {},
    })
  }
  const [postResponse, doPost] = useApi(postFamilyMember, { successCallback })
  const [patchResponse, doPatch] = useApi(patchFamilyMember, { successCallback })

  const isLoading =
    patchResponse.status === FetchState.LOADING ||
    postResponse.status === FetchState.LOADING ||
    ticketRightsResponse.status === FetchState.LOADING ||
    patchPreferenceResponse.status === FetchState.LOADING

  const isError =
    postResponse.status === FetchState.ERROR ||
    patchResponse.status === FetchState.ERROR ||
    patchPreferenceResponse.status === FetchState.ERROR

  const isChangeTicketTypeButtonEnabled =
    props.familyMember?.customerNumber && !isSeniorCitizenByAge(props?.familyMember.dateOfBirth)

  const isEditing = !!props.familyMember
  const canEditRelation = props.familyMember?.relation.code && props.familyMember?.relation.code !== 'Barn'

  // Reset on open
  useEffect(() => {
    setState(initialState(props.familyMember))
    setTriedToSave(false)
  }, [props.familyMember, props.open])

  const validateAllAndFetch = (callback: () => void) => {
    const localState = validateAllFields(state)
    if (formIsValid(localState)) {
      callback()
    }
    setState(localState)
    setTriedToSave(true)
  }

  const post = (e: FormEvent) => {
    e.preventDefault()
    trackEvent('Create-Family-Member', { category: 'Family-Member', relation: state.relation.value })
    validateAllAndFetch(() =>
      doPost({
        id: props.employee.id,
        body: body(state),
      })
    )
  }

  const patch = (e: FormEvent) => {
    e.preventDefault()
    trackEvent('Update-Family-Member', { category: 'Family-Member', relation: state.relation.value })
    validateAllAndFetch(() => {
      doPatch({
        id: props.familyMember?.id,
        body: body(state),
      })

      if (props.familyMember?.customerNumber) {
        const preference = ticketTypePreference(state.ticketType.value)
        const existing = props.familyMember?.customerPreferences?.ticketTypePreference

        if (preference !== existing) {
          doPatchPreference({
            id: `${props.familyMember.customerNumber}`,
            body: { ticketTypePreference: preference },
          })
        }
      }
    })
  }

  return (
    <Modal
      className="family-member-modal"
      data-cy="family-member-modal"
      open={props.open}
      title={isEditing ? 'Rediger familiemedlem' : 'Nytt familiemedlem'}
      size="large"
      onDismiss={() => props.close(false)}
    >
      <form onSubmit={isEditing ? patch : post}>
        <GridContainer spacing="medium" className="spacing-top-medium">
          <GridItem small={12} medium={6} className="spacing-bottom-medium">
            <TextField
              label="Rettighetseier"
              readOnly
              value={`${props.employee.surname}, ${props.employee.firstName}`}
            />
          </GridItem>
          <GridItem small={12} medium={6} className="spacing-bottom-medium">
            <ValidatableChoiceChipGroup
              field={state.relation}
              update={(value) =>
                setState({
                  ...state,
                  relation: value,
                })
              }
            >
              <ChoiceChip
                data-cy="relation-chip-spouse"
                value="Ektefelle"
                disabled={isEditing && !canEditRelation}
              >
                Ektefelle
              </ChoiceChip>
              <ChoiceChip data-cy="relation-chip-partner" value="Samboer" disabled={isEditing && !canEditRelation}>
                Samboer
              </ChoiceChip>
              <ChoiceChip data-cy="relation-chip-child" value="Barn" disabled={isEditing}>
                Barn
              </ChoiceChip>
            </ValidatableChoiceChipGroup>
          </GridItem>

          <GridItem small={12} medium={6} className="spacing-bottom-medium">
            <ValidatableTextField
              field={state.firstName}
              update={(value) =>
                setState({
                  ...state,
                  firstName: value,
                })
              }
            />
          </GridItem>
          <GridItem small={12} medium={6} className="spacing-bottom-medium">
            <ValidatableTextField
              field={state.surname}
              update={(value) =>
                setState({
                  ...state,
                  surname: value,
                })
              }
            />
          </GridItem>

          <GridItem small={12} medium={6}>
            <GridContainer spacing="small">
              <GridItem small={12} medium={6} className="spacing-bottom-medium">
                <ValidatableDatePicker
                  date={state.dateOfBirth}
                  update={(value) =>
                    setState({
                      ...state,
                      dateOfBirth: value,
                    })
                  }
                />
              </GridItem>

              <GridItem small={12} medium={6} className="spacing-bottom-medium">
                <ValidatableTextField
                  field={state.ssn}
                  update={(value) =>
                    setState({
                      ...state,
                      ssn: value,
                    })
                  }
                />
              </GridItem>

              {state.relation.value !== 'Barn' && (
                <GridItem small={12} medium={6} className="spacing-bottom-medium">
                  <OptionalTooltipWrapper
                    placement="right"
                    tooltipText={
                      !props.familyMember?.customerNumber
                        ? 'Familiemedlem har ingen synkroniserte rettigheter'
                        : 'Familiemedlem er 67 år eller eldre'
                    }
                    enabled={true}
                  >
                    <div data-cy="edit-family-modal-ticket-type-dropdown">
                      <TicketTypeDropdown
                        disabled={!isChangeTicketTypeButtonEnabled}
                        items={ticketTypePreferenceItems}
                        field={state.ticketType}
                        update={(value) => {
                          setState({
                            ...state,
                            ticketType: value,
                          })
                        }}
                      />
                    </div>
                  </OptionalTooltipWrapper>
                </GridItem>
              )}

              <GridItem small={12} medium={6} className="spacing-bottom-medium">
                {state.code.value && (
                  <TextField
                    label={state.code.displayName}
                    name="ticketHolderCode"
                    value={state.code.value}
                    readOnly
                  />
                )}
              </GridItem>
            </GridContainer>
          </GridItem>

          <GridItem medium={12} className="spacing-bottom-small" />

          <GridItem small={12} className="spacing-top-extra-large5" />

          {triedToSave && !formIsValid(state) && (
            <GridItem small={12}>
              <SmallAlertBox variant="error" width="fit-content">
                Alle felt må være fylt ut med gyldige verdier for å sende inn skjemaet.
              </SmallAlertBox>
            </GridItem>
          )}

          {isError && (
            <GridItem small={12}>
              <BannerAlertBox
                className="spacing-bottom-large"
                variant="error"
                title="Lagring feilet"
                closable={false}
              >
                Prøv på nytt eller ta kontakt med Entur support om det vedvarer.
              </BannerAlertBox>
            </GridItem>
          )}

          {ticketRightsResponse.status === FetchState.ERROR && (
            <GridItem small={12}>
              <BannerAlertBox
                className="spacing-bottom-large"
                variant="error"
                title="Kunne ikke generere nye rettigheter"
              >
                En teknisk feil gjorde at endringene ble registrert, men rettighet ikke oppdatert.
              </BannerAlertBox>
            </GridItem>
          )}

          <GridItem small={12}>
            <PrimaryButton className="spacing-right-medium" type="submit" loading={isLoading}>
              {isEditing ? 'Lagre familiemedlem' : 'Legg til familiemedlem'}
            </PrimaryButton>
            <SecondaryButton onClick={() => props.close(false)}>
              {isEditing ? 'Forkast endringer' : 'Forkast familiemedlem'}
            </SecondaryButton>
          </GridItem>
        </GridContainer>
      </form>
    </Modal>
  )
}

export default EditFamilyMemberModal
